import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { Subscription } from 'rxjs';
import { ImageService } from '../../services/image.service';
import { siteConfig } from '../../config/site-config';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, FormsModule, CommonModule, NgOptimizedImage],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css'
})
export class NavbarComponent implements OnInit, OnDestroy {
  readonly logoImage = siteConfig.assets.logoImageName;
  readonly logoAltText = `${siteConfig.brand.displayName} Logo`;
  readonly seasonCalendarPath = siteConfig.routes.seasonCalendarPath;
  readonly lexiconOverviewPath = siteConfig.routes.lexiconOverviewPath;
  readonly blogPath = siteConfig.routes.blogPath;
  readonly aboutPath = siteConfig.routes.aboutPath;
  readonly infoCategorySlug = siteConfig.content.defaultInfoCategorySlug;

  isMobileMenuOpen = false;
  isSearchActive = false;
  searchQuery = '';
  isNavbarHidden = false;
  lastScrollTop = 0;

  isAuthor = false;
  isAuthenticated = false; // Status der Authentifizierung
  private authSubscription: Subscription = new Subscription();

  constructor(
    private router: Router,
    private authService: AuthService,
    public imageService: ImageService
  ) {}
  
  ngOnInit(): void {
    // Initial prüfen, ob der Benutzer authentifiziert ist
    this.isAuthenticated = this.authService.isAuthenticated();
    
    // Auf Änderungen des Authentifizierungsstatus achten
    this.authSubscription = this.authService.authState$.subscribe(
      isAuthenticated => {
        this.isAuthenticated = isAuthenticated;
        
        // Hier kannst du auch den Benutzer abrufen und prüfen, ob er ein Autor ist
        if (isAuthenticated) {
          const user = this.authService.getCurrentUser();
          this.isAuthor = user?.role === 'author' || user?.role === 'admin';
        } else {
          this.isAuthor = false;
        }
      }
    );
  }
  
  ngOnDestroy(): void {
    // Subscription aufräumen, um Memory Leaks zu vermeiden
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }
  
  @HostListener('window:scroll')
  onWindowScroll() {
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // Navbar verstecken beim Runterscrollen, anzeigen beim Hochscrollen
    if (currentScrollTop > this.lastScrollTop && currentScrollTop > 100 && !this.isMobileMenuOpen) {
      this.isNavbarHidden = true;
    } else {
      this.isNavbarHidden = false;
    }
    
    this.lastScrollTop = currentScrollTop;
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    
    // Schließe Suche, wenn Mobilmenü geöffnet wird
    if (this.isMobileMenuOpen && this.isSearchActive) {
      this.isSearchActive = false;
    }
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }
  
  toggleSearch(): void {
    this.isSearchActive = !this.isSearchActive;
    
    // Schließe Mobilmenü, wenn Suche geöffnet wird
    if (this.isSearchActive && this.isMobileMenuOpen) {
      this.isMobileMenuOpen = false;
    }
    
    // Zurücksetzen der Suche beim Schließen
    if (!this.isSearchActive) {
      this.searchQuery = '';
    }
  }
  
  handleSearch(event: Event): void {
    event.preventDefault();
    if (this.searchQuery.trim()) {
      this.router.navigate(['/suche'], { 
        queryParams: { q: this.searchQuery.trim() } 
      });
      this.toggleSearch();
    }
  }
  
  logout(): void {
    this.authService.logout();
  }
  
  // Falls du das Kräuter-des-Tages dynamisch machen möchtest,
  // könntest du diese Funktion hinzufügen:
  
  // Optionale Methode für dynamisches Kraut des Tages
  getTodaysHerb() {
    const herbs = [
      { name: 'Basilikum', route: `/${this.infoCategorySlug}/basilikum` },
      { name: 'Thymian', route: `/${this.infoCategorySlug}/thymian` },
      { name: 'Rosmarin', route: `/${this.infoCategorySlug}/rosmarin` },
      { name: 'Minze', route: `/${this.infoCategorySlug}/minze` },
      { name: 'Salbei', route: `/${this.infoCategorySlug}/salbei` }
    ];
    
    // Einfache Berechnung basierend auf dem Tag des Monats
    const today = new Date();
    const dayOfMonth = today.getDate();
    const herbIndex = dayOfMonth % herbs.length;
    
    return herbs[herbIndex];
  }
}
